import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

export function cx(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(" ");
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly loading?: boolean;
  readonly full?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-white hover:bg-accent-hover border-transparent",
  secondary: "bg-surface text-ink border-line-strong hover:bg-surface-2",
  ghost: "bg-transparent text-ink-soft border-transparent hover:bg-surface-2 hover:text-ink",
  danger: "bg-warn text-white hover:brightness-110 border-transparent",
};

export function Button({
  variant = "primary",
  loading = false,
  full = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled === true || loading}
      // aria-busy rather than swapping the label out: a screen reader user
      // should not lose the button's name while it is working.
      aria-busy={loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2",
        "text-[15px] font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-55",
        full && "w-full",
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
}

export function Spinner({ className }: { readonly className?: string }) {
  return (
    <svg
      className={cx("animate-spin", className ?? "h-5 w-5")}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function Card({
  children,
  className,
  as: Tag = "div",
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly as?: "div" | "li" | "section" | "article";
}) {
  return (
    <Tag className={cx("rounded-xl border border-line bg-surface", className)}>{children}</Tag>
  );
}

type Tone = "neutral" | "accent" | "warn" | "caution";

const TONES: Record<Tone, string> = {
  neutral: "bg-surface-2 text-ink-soft border-line",
  accent: "bg-accent-soft text-accent border-accent/25",
  warn: "bg-warn-soft text-warn border-warn/25",
  caution: "bg-caution-soft text-caution border-caution/25",
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: {
  readonly tone?: Tone;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5",
        "text-xs font-medium whitespace-nowrap",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Alert({
  tone = "warn",
  title,
  children,
}: {
  readonly tone?: Tone;
  readonly title?: string;
  readonly children: ReactNode;
}) {
  return (
    <div
      // Assertive would interrupt a screen reader mid-sentence; these are
      // important but not emergencies.
      role="status"
      className={cx("rounded-lg border px-4 py-3 text-[14.5px] leading-relaxed", TONES[tone])}
    >
      {title !== undefined && <p className="mb-1 font-semibold">{title}</p>}
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  readonly label: string;
  // `| undefined` rather than bare optional: under exactOptionalPropertyTypes
  // these are different types, and every caller computes these conditionally.
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly children: ReactNode;
  readonly required?: boolean | undefined;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline gap-1.5 text-[14px] font-medium text-ink">
        {label}
        {required === true && (
          <span className="text-ink-faint text-xs font-normal">required</span>
        )}
      </span>
      {hint !== undefined && <p className="text-ink-faint mb-1.5 text-[13px]">{hint}</p>}
      {children}
      {error !== undefined && (
        <p className="text-warn mt-1.5 text-[13px] font-medium">{error}</p>
      )}
    </label>
  );
}

const CONTROL =
  "w-full rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] text-ink " +
  "placeholder:text-ink-faint focus:border-accent focus:outline-none " +
  "disabled:bg-surface-2 disabled:text-ink-faint";

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(CONTROL, className)} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cx(CONTROL, "resize-y leading-relaxed", className)} />;
}

export function Select({
  className,
  children,
  ...rest
}: InputHTMLAttributes<HTMLSelectElement> & { readonly children: ReactNode }) {
  return (
    <select {...rest} className={cx(CONTROL, "cursor-pointer", className)}>
      {children}
    </select>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <Card className="px-6 py-12 text-center">
      <p className="text-[16px] font-medium text-ink">{title}</p>
      {children !== undefined && (
        <p className="text-ink-soft mx-auto mt-2 max-w-md text-[14.5px] leading-relaxed">
          {children}
        </p>
      )}
      {action !== undefined && <div className="mt-5">{action}</div>}
    </Card>
  );
}

export function Loading({ label = "Loading" }: { readonly label?: string }) {
  return (
    <div className="text-ink-faint flex items-center justify-center gap-2.5 py-16">
      <Spinner />
      <span className="text-[14.5px]">{label}…</span>
    </div>
  );
}

export function ErrorNote({ error }: { readonly error: unknown }) {
  const message =
    error instanceof Error ? error.message : "Something went wrong. Please try again.";

  return (
    <Alert tone="warn" title="That did not work">
      {message}
    </Alert>
  );
}

export function PageHeader({
  eyebrow,
  title,
  children,
  actions,
}: {
  readonly eyebrow?: string;
  readonly title: string;
  readonly children?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <header className="border-line-strong mb-7 flex flex-wrap items-end justify-between gap-4 border-b pb-5">
      <div>
        {eyebrow !== undefined && (
          <p className="text-accent font-mono text-[11.5px] font-medium tracking-[0.08em] uppercase">
            {eyebrow}
          </p>
        )}
        <h1 className="mt-1.5 text-[26px] leading-tight font-semibold tracking-[-0.01em]">
          {title}
        </h1>
        {children !== undefined && (
          <p className="text-ink-soft mt-2 max-w-2xl text-[15px] leading-relaxed">{children}</p>
        )}
      </div>
      {actions !== undefined && <div className="flex gap-2">{actions}</div>}
    </header>
  );
}
